import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'
import { showBanner } from '../../global-state/banner-slice'
import { transactionServices } from '../../services'
import type {
  DividendDeclarationPayload,
  DividendPayoutPreviewResponse,
  DividendPayoutResponse,
} from '../../services/transaction-service'
import { DividendPayoutFormPresenter } from './dividend-payout-form-presenter'

const DividendPayoutFormContainer = () => {
  const dispatch = useDispatch()
  const [displayUploadModal, setDisplayUploadModal] = useState(false)
  const [dividendFiles, setDividendFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previews, setPreviews] = useState<DividendPayoutPreviewResponse[]>([])
  const [pendingDeclarations, setPendingDeclarations] = useState<DividendDeclarationPayload[]>([])
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [results, setResults] = useState<DividendPayoutResponse[]>([])
  const [resultError, setResultError] = useState<string | null>(null)

  const handleUpload = useCallback(async () => {
    if (!dividendFiles.length) {
      dispatch(
        showBanner({
          text: 'Please select a CSV file to upload',
          variant: 'error-banner',
        }),
      )
      return
    }

    const file = dividendFiles[0]
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      dispatch(
        showBanner({
          text: 'Please upload a valid CSV file',
          variant: 'error-banner',
        }),
      )
      return
    }

    setIsUploading(true)
    setPreviewLoading(true)
    setPreviewError(null)
    setResults([])
    setResultError(null)
    try {
      const formData = new FormData()
      formData.append('file', file)
      const response = await transactionServices.uploadDividendDeclarationsCsv(formData)
      if (
        response.status === 200 &&
        response.data?.declarations?.length &&
        response.data?.previews?.length
      ) {
        setDisplayUploadModal(false)
        setDividendFiles([])
        setPendingDeclarations(response.data.declarations)
        setPreviews(response.data.previews)
        setConfirmOpen(true)
        dispatch(
          showBanner({
            text: `Preview ready for ${response.data.rowsProcessed} dividend declaration(s). Confirm to process payouts.`,
            variant: 'success-banner',
          }),
        )
      } else {
        const errorMessage = response.error || 'Failed to upload dividend CSV.'
        setPreviewError(errorMessage)
        dispatch(
          showBanner({
            text: errorMessage,
            variant: 'error-banner',
          }),
        )
      }
    } catch {
      setPreviewError('Error uploading file. Please try again.')
      dispatch(
        showBanner({
          text: 'Error uploading file. Please try again.',
          variant: 'error-banner',
        }),
      )
    } finally {
      setIsUploading(false)
      setPreviewLoading(false)
    }
  }, [dispatch, dividendFiles])

  const handleConfirmExecute = useCallback(async () => {
    if (!pendingDeclarations.length) return
    setIsSubmitting(true)
    setResultError(null)
    setResults([])
    try {
      const created: DividendPayoutResponse[] = []
      for (const declaration of pendingDeclarations) {
        const response = await transactionServices.createDividendPayouts({
          declaration,
        })
        if (response.status === 200 && response.data) {
          created.push(response.data)
        } else {
          const errorMessage = response.error || 'Failed to process dividend payout.'
          setResultError(errorMessage)
          dispatch(
            showBanner({
              text: errorMessage,
              variant: 'error-banner',
            }),
          )
          setIsSubmitting(false)
          return
        }
      }
      setResults(created)
      setConfirmOpen(false)
      setPreviews([])
      setPendingDeclarations([])
      dispatch(
        showBanner({
          text: 'Dividend payouts processed successfully.',
          variant: 'success-banner',
        }),
      )
    } catch {
      const errorMessage = 'Failed to process dividend payout. Please try again.'
      setResultError(errorMessage)
      dispatch(
        showBanner({
          text: errorMessage,
          variant: 'error-banner',
        }),
      )
    } finally {
      setIsSubmitting(false)
    }
  }, [pendingDeclarations, dispatch])

  const handleCloseConfirm = useCallback(() => {
    setConfirmOpen(false)
    // Cancel discards in-memory preview only — nothing was persisted on upload
    setPreviews([])
    setPendingDeclarations([])
    setPreviewError(null)
  }, [])

  return (
    <DividendPayoutFormPresenter
      displayUploadModal={displayUploadModal}
      onOpenUploadModal={() => setDisplayUploadModal(true)}
      onCloseUploadModal={() => {
        setDisplayUploadModal(false)
        setDividendFiles([])
      }}
      dividendFiles={dividendFiles}
      onDividendFilesChange={setDividendFiles}
      onUpload={handleUpload}
      isUploading={isUploading}
      previewLoading={previewLoading}
      previewError={previewError}
      previews={previews}
      confirmOpen={confirmOpen}
      onOpenConfirm={() => setConfirmOpen(true)}
      onCloseConfirm={handleCloseConfirm}
      onConfirmExecute={handleConfirmExecute}
      isSubmitting={isSubmitting}
      results={results}
      resultError={resultError}
    />
  )
}

export { DividendPayoutFormContainer }
